import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Card, Grid, GridContainer, Header, InputDropdown, Row, Text } from '@plurall/elo-beta'
import { CurrentPeriod, PeriodContext } from 'App/components/CurrentPeriod'
import Loading from 'App/components/Loading/Loading'
import MainMenu from 'App/components/MainMenu/MainMenu'
import { PERSON_CLASS_AND_DISCIPLINES } from 'graphql/queries'
import PropTypes from 'prop-types'
import UserContext from 'stores/UserStore'

import { POSITION_CLASS_STATUS_ID, ROLE_TEACHER_ID } from 'utils'
import { graphqlClient } from 'utils/graphqlClient'

import LoadValidSchools from './LoadValidSchools'
import styles from './SchoolData.module.scss'
import { SchoolRoles } from './SchoolRoles'
import ValidSchoolsContext from './ValidSchoolsContext'

const SchoolData = () => {
  const navigate = useNavigate()

  useEffect(() => {
    const { search } = window.location
    const params = new URLSearchParams(search)
    const redirect = params.get('redirect')

    if (redirect) {
      window.PLURALL_ELO_TOAST_CONTAINER.addToast({
        variant: 'positive',
        text: 'Atualização realizada com sucesso',
      })

      params.delete('redirect')
      const updatedSearch = params.toString()
      navigate({ search: updatedSearch })
    }
  }, [navigate])

  return (
    <CurrentPeriod>
      <LoadValidSchools>
        <SchoolDataView />
      </LoadValidSchools>
    </CurrentPeriod>
  )
}

const SchoolDataView = () => {
  const [currentSchool, setCurrentSchool] = useState(null)
  const userStore = useContext(UserContext)
  const periodIds = [...useContext(PeriodContext)]
  const validSchoolsData = useContext(ValidSchoolsContext)
  const schoolData = userSchoolData(validSchoolsData, currentSchool)
  const [loadingSchool, setLoadingSchool] = useState(false)
  const [hasClasses, setHasClasses] = useState(false)

  useEffect(() => {
    userStore.clearLocalStorage('current_school')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCurrentSchool = useCallback(
    async e => {
      const filter = userStore?.person?.schools?.find(school => school.id === e)

      const schoolInfo = filter || null
      if (schoolInfo) {
        schoolInfo.accessCode = filter ? filter?.accessCode : null
      }

      const roles = await getCurrentSchool(schoolInfo?.id)

      if (roles) {
        const { roles: removeRolesFromObject, ...rest } = schoolInfo

        userStore.setCurrentSchool(rest)

        schoolInfo.roles = roles
        setCurrentSchool(schoolInfo)
        checkClasses(roles)
      }
    },
    [userStore],
  )

  const checkClasses = roles => {
    const role = roles.find(role => +role.id === ROLE_TEACHER_ID)

    if (role) {
      const hasClass = role.levels.some(
        level =>
          level.disciplines?.some(discipline => discipline.classes?.length) ||
          level.grades?.some(grade => grade.knowlegedAreas?.length),
      )

      setHasClasses(hasClass)
    }
  }

  useEffect(() => {
    if (!currentSchool) {
      const school = userStore.getCurrentSchool()
      if (school) {
        setCurrentSchool(school)
        handleCurrentSchool(school.id)
      }
    }
  }, [currentSchool, setCurrentSchool, userStore, handleCurrentSchool])

  const getCurrentSchool = async id => {
    try {
      setLoadingSchool(true)
      const {
        data: {
          person: { roles },
        },
      } = await graphqlClient().query({
        query: PERSON_CLASS_AND_DISCIPLINES,
        variables: {
          schoolId: +id,
          positionClassStatusId: POSITION_CLASS_STATUS_ID,
          periodIds: periodIds.map(period => +period.id),
        },
        fetchPolicy: 'internet-only',
      })

      return roles
    } catch (error) {
      window.PLURALL_ELO_TOAST_CONTAINER.addToast({
        variant: 'negative',
        text: 'Ocorreu um erro ao carregar as informações da escola',
      })
    } finally {
      setLoadingSchool(false)
    }
  }

  return (
    <SchoolDataRender
      schoolData={schoolData}
      currentSchool={currentSchool}
      handleCurrentSchool={handleCurrentSchool}
      userStore={userStore}
      loadingSchool={loadingSchool}
      hasClasses={hasClasses}
    />
  )
}

const userSchoolData = (schools, currentSchool) =>
  schools.map(({ id, tradeName, legalName }) => ({
    label: tradeName || legalName,
    id,
    checked: id === currentSchool?.id,
  }))

const SchoolSelection = ({ schoolData, handleCurrentSchool }) => (
  <GridContainer>
    <Row>
      <Grid desktop={12}>
        <Card>
          <div className={styles['school-selection']}>
            <div className={styles['school-selection-title']}>
              <Text bold> Escola </Text>
            </div>
            {schoolData && (
              <InputDropdown
                dataTestId='dropdown-school-selection'
                items={schoolData}
                placeholder='Selecione uma escola'
                onChange={e => handleCurrentSchool(e.id)}
              />
            )}
          </div>
        </Card>
      </Grid>
    </Row>
  </GridContainer>
)

const SchoolDataRender = ({
  schoolData,
  currentSchool,
  handleCurrentSchool,
  userStore,
  loadingSchool,
  ...rest
}) => (
  <>
    <Header description='Visualize e gerencie suas informações relacionadas a escola.'>
      Dados escolares
    </Header>

    <GridContainer>
      <Row>
        <MainMenu />
        <Grid desktop={8} mobile={12}>
          <SchoolSelection handleCurrentSchool={handleCurrentSchool} schoolData={schoolData} />
          {loadingSchool ? (
            <Loading />
          ) : (
            currentSchool && (
              <SchoolRoles currentSchool={currentSchool} userStore={userStore} {...rest} />
            )
          )}
        </Grid>
      </Row>
    </GridContainer>
  </>
)

SchoolSelection.propTypes = {
  schoolData: PropTypes.array,
  currentSchool: PropTypes.object,
  handleCurrentSchool: PropTypes.func,
}

SchoolDataRender.propTypes = {
  schoolData: PropTypes.array,
  currentSchool: PropTypes.object,
  handleCurrentSchool: PropTypes.func,
  userStore: PropTypes.object,
  loadingSchool: PropTypes.bool,
}

export default SchoolData
